// src/components/graphs/DefectsPerProductVersion.js

import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { AuthContext } from 'contexts/auth_context/AuthContext';
import axios from 'axios';
import { BASE_URL } from 'BaseURL';
import { COLORS } from 'constants/colors';
import Spinner from 'components/Spinner';
import 'chartjs-plugin-datalabels';
import './ChartStyles.css';

const DefectsPerProductVersion = ({ filter }) => {
    const [chartData, setChartData] = useState(null);
    const { token } = useContext(AuthContext);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDefectsPerProductVersion = async () => {
            try {
                const params = {
                    ...(filter.vehicleModel && { vehicle_model: filter.vehicleModel }),
                    ...(filter.startDate && { start_date: filter.startDate }),
                    ...(filter.endDate && { end_date: filter.endDate }),
                };

                const response = await axios.get(
                    `${BASE_URL}shiftlens_warehouse/api/defects-per-product-version/`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params,
                    }
                );

                const data = response.data;

                // Prepare data for the stacked bar chart
                const periodsSet = new Set();
                data.forEach((item) => {
                    item.defects_over_time.forEach((defect) => {
                        periodsSet.add(defect.period);
                    });
                });
                const periods = Array.from(periodsSet).sort();

                const datasets = data.map((item, index) => {
                    const dataPoints = periods.map((period) => {
                        const defect = item.defects_over_time.find(
                            (d) => d.period === period
                        );
                        return defect ? defect.defect_count : 0;
                    });

                    return {
                        label: item.vehicle_model_and_version,
                        data: dataPoints,
                        backgroundColor: COLORS[index % COLORS.length],
                    };
                });

                setChartData({
                    labels: periods,
                    datasets,
                });
            } catch {
                setError('Failed to load data.');
            }
        };

        fetchDefectsPerProductVersion();
    }, [token, filter]);

    const options = useMemo(
        () => ({
            maintainAspectRatio: false,
            responsive: true,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            plugins: {
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                        label: function (context) {
                            const value = context.raw;
                            if (value === 0) {
                                return ''; // Hide tooltip for zero values
                            }
                            return `${context.dataset.label}: ${value}`;
                        },
                    },
                },
                legend: {
                    position: 'top',
                    labels: {
                        font: {
                            size: 10,
                        },
                    },
                },
                datalabels: {
                    display: function (context) {
                        return context.dataset.data[context.dataIndex] !== 0;
                    },
                    color: 'black',
                    font: {
                        weight: 'bold',
                    },
                },
            },
            scales: {
                x: {
                    stacked: true,
                    title: { display: true, text: 'Period (Year-Month)' },
                    ticks: {
                        maxRotation: 90,
                        minRotation: 45,
                        autoSkip: false,
                        font: {
                            size: 10,
                        },
                    },
                },
                y: {
                    stacked: true,
                    title: { display: true, text: 'Defect Count' },
                    beginAtZero: true,
                    ticks: {
                        font: {
                            size: 10,
                        },
                        callback: function (value) {
                            if (value === 0) {
                                return ''; // Hide '0' tick label
                            }
                            return value;
                        },
                    },
                },
            },
        }),
        []
    );

    return (
        <div className="chart-wrapper chart-height">
            {error && <p className="error-message">{error}</p>}
            {chartData ? (
                <Bar data={chartData} options={options} />
            ) : (
                <Spinner />
            )}
        </div>
    );
};

export default React.memo(DefectsPerProductVersion);
